import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { STLLoader }           from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls }       from 'three/examples/jsm/controls/OrbitControls';

export default function StlViewer({ url, width = 600, height = 400, showTools= false }) {
  const mountRef = useRef(null);

  useEffect(() => {
    // ── basic three.js scene ───────────────────────────────────────
    const scene    = new THREE.Scene();
    const camera   = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 120);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setClearColor(0xeeeeee);  // light gray background

    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

   /* ── lights ────────────────────────────────────────────── */

    // 1) soft overall illumination
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // 2) key light – above/right/front of the model
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.9);
    keyLight.position.set(1, 1, 1).multiplyScalar(150); // x, y, z then scale outwards
    keyLight.lookAt(0, 0, 0);
    scene.add(keyLight);

    // 3) fill light – opposite side, slightly below, softer
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
    fillLight.position.set(-1, -0.4, -1).multiplyScalar(150); // below & across
    fillLight.lookAt(0, 0, 0);
    scene.add(fillLight);


    // controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = false;

    // ── load STL ───────────────────────────────────────────────────
    new STLLoader().load(url, geometry => {
      const material = new THREE.MeshStandardMaterial({ color: 0xcccccc, flatShading: true });
      const mesh = new THREE.Mesh(geometry, material);

      // center & scale reasonably
      geometry.computeBoundingBox();
      const { max, min } = geometry.boundingBox;
      const size   = new THREE.Vector3().subVectors(max, min).length();
      const center = new THREE.Vector3().addVectors(max, min).multiplyScalar(0.5);
      mesh.geometry.translate(-center.x, -center.y, -center.z);
      const scale = 80 / size;          // fit into view
      mesh.scale.setScalar(scale);

      scene.add(mesh);
      render();
    });

    // render loop
    function render() {
      renderer.render(scene, camera);
    }
    controls.addEventListener('change', render);   // re-render on user interaction

    // cleanup
    return () => {
      // slight delay so modal unmount animations finish first
      setTimeout(() => {
        controls.dispose();
        renderer.dispose();
        if (mountRef.current && renderer.domElement) {
          mountRef.current.removeChild(renderer.domElement);
        }
      }, 1000); // 0.5 s delay
    };
  }, [url, width, height]);

  return <div className='flex flex-col gap-y-2'>
    <div ref={mountRef} style={{ width, height }} /> 
    {/* <div className='flex justify-end'>
      <input
        type="range"
        min="0"
        max="360"
        value={angle}
        onChange={e => setAngle(Number(e.target.value))}
        style={{ width }}
      />
    </div> */}
  </div>;
}


// StlViewerWithTools.jsx
// Revised: better centring/fit. Mesh is centred at origin, camera fits to the
// model's largest dimension, and OrbitControls.target follows the model so the
// object is always in view. Tools UI unchanged.
// import { useEffect, useRef, useState } from 'react';
// import * as THREE                  from 'three';
// import { STLLoader }               from 'three/examples/jsm/loaders/STLLoader';
// import { OrbitControls }           from 'three/examples/jsm/controls/OrbitControls';

// export default function StlViewer({
//   url,
//   width  = 800,
//   height = 600,
//   showTools = false,
// }) {
//   const mountRef          = useRef(null);
//   const [wireframe, setWireframe] = useState(false);
//   const [matColor,  setMatColor]  = useState('#cccccc');

//   /* light state */
//   const [ambientI, setAmbientI] = useState(0.4);
//   const [keyI,     setKeyI]     = useState(0.8);
//   const [fillI,    setFillI]    = useState(0.5);

//   const [keyAngle, setKeyAngle]  = useState(45);
//   const [fillAngle, setFillAngle] = useState(-135);
//   const [keyHue,   setKeyHue]    = useState('#ffffff');
//   const [fillHue,  setFillHue]   = useState('#ffffff');

//   useEffect(() => {
//     if (!mountRef.current) return;

//     /* scene, cam, renderer */
//     const scene    = new THREE.Scene();
//     const camera   = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);

//     const renderer = new THREE.WebGLRenderer({ antialias: true });
//     renderer.setSize(width, height);
//     renderer.setClearColor(0xeeeeee);
//     mountRef.current.appendChild(renderer.domElement);

//     /* ground */
//     const ground = new THREE.Mesh(
//       new THREE.PlaneGeometry(400, 400),
//       new THREE.MeshStandardMaterial({ color: 0x161616 })
//     );
//     ground.rotation.x = -Math.PI / 2;
//     ground.position.y = -40;
//     scene.add(ground);

//     /* lights */
//     const ambient = new THREE.AmbientLight(0xffffff, ambientI);
//     scene.add(ambient);

//     const key  = new THREE.DirectionalLight(new THREE.Color(keyHue), keyI);
//     scene.add(key);
//     const fill = new THREE.DirectionalLight(new THREE.Color(fillHue), fillI);
//     scene.add(fill);

//     /* controls */
//     const controls = new OrbitControls(camera, renderer.domElement);
//     controls.enablePan = false;

//     let mesh;

//     const updateLights = (bboxSize = 100) => {
//       ambient.intensity = ambientI;

//       const ka = (keyAngle * Math.PI) / 180;
//       const fa = (fillAngle * Math.PI) / 180;

//       key.color.set(keyHue);
//       key.intensity = keyI;
//       key.position.set(Math.sin(ka) * bboxSize, bboxSize * 0.6, Math.cos(ka) * bboxSize);
//       key.lookAt(0, 0, 0);

//       fill.color.set(fillHue);
//       fill.intensity = fillI;
//       fill.position.set(Math.sin(fa) * bboxSize, -bboxSize * 0.4, Math.cos(fa) * bboxSize);
//       fill.lookAt(0, 0, 0);
//     };

//     /* STL */
//     new STLLoader().load(url, geometry => {
//       geometry.computeBoundingBox();
//       const bbox   = geometry.boundingBox;
//       const size   = new THREE.Vector3();
//       bbox.getSize(size);
//       const maxDim = Math.max(size.x, size.y, size.z);

//       // centre geometry at origin
//       const center = new THREE.Vector3();
//       bbox.getCenter(center);
//       geometry.translate(-center.x, -center.y, -center.z);

//       mesh = new THREE.Mesh(
//         geometry,
//         new THREE.MeshStandardMaterial({ color: matColor, flatShading: true, wireframe })
//       );
//       mesh.rotation.x = Math.PI / 2;
//       const scale = 80 / maxDim; // fit object roughly into view volume
//       mesh.scale.setScalar(scale);
//       scene.add(mesh);

//       /* camera fit */
//       camera.position.set(0, 0, 120);
//       camera.near = 0.1;
//       camera.far  = 5000;
//       camera.lookAt(0, 0, 0);
//       controls.target.set(0, 0, 0);
//       controls.update();

//       updateLights(maxDim);
//       renderer.render(scene, camera);
//     });

//     controls.addEventListener('change', () => renderer.render(scene, camera));

//     const rerender = () => {
//       if (mesh) {
//         mesh.material.color.set(matColor);
//         mesh.material.wireframe = wireframe;
//         mesh.material.needsUpdate = true;
//       }
//       updateLights();
//       renderer.render(scene, camera);
//     };

//     rerender();

//   return () => {
//       // slight delay so modal unmount animations finish first
//       setTimeout(() => {
//         controls.dispose();
//         renderer.dispose();
//         if (mountRef.current && renderer.domElement) {
//           mountRef.current.removeChild(renderer.domElement);
//         }
//       }, 500); // 0.5 s delay
//     };
//   }, [url, width, height, ambientI, keyI, fillI, keyAngle, fillAngle, keyHue, fillHue, matColor, wireframe]);

//   /* tools panel */
//   const tools = showTools && (
//     <div style={{ flex: 1, padding: '0 12px', fontFamily: 'sans-serif' }}>
//       <h4>Lights</h4>
//       <fieldset>
//         <legend>Key</legend>
//         <input type="range" min="0" max="360" value={keyAngle} onChange={e => setKeyAngle(+e.target.value)} />
//         <input type="range" min="0" max="2" step="0.1" value={keyI} onChange={e => setKeyI(+e.target.value)} />
//         <input type="color" value={keyHue} onChange={e => setKeyHue(e.target.value)} />
//       </fieldset>
//       <fieldset>
//         <legend>Fill</legend>
//         <input type="range" min="0" max="360" value={fillAngle} onChange={e => setFillAngle(+e.target.value)} />
//         <input type="range" min="0" max="2" step="0.1" value={fillI} onChange={e => setFillI(+e.target.value)} />
//         <input type="color" value={fillHue} onChange={e => setFillHue(e.target.value)} />
//       </fieldset>
//       <fieldset>
//         <legend>Material</legend>
//         <input type="color" value={matColor} onChange={e => setMatColor(e.target.value)} />
//         <label>
//           Wireframe
//           <input type="checkbox" checked={wireframe} onChange={e => setWireframe(e.target.checked)} />
//         </label>
//       </fieldset>
//     </div>
//   );

//   const viewer = <div ref={mountRef} style={{ width: showTools ? '100%' : width, height }} />;

//   return showTools ? (
//     <div style={{ display: 'flex', width, height }}>
//       <div style={{ flex: 4 }}>{viewer}</div>
//       {tools}
//     </div>
//   ) : (
//     viewer
//   );
// }